import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { cohortQueryKeys, fetchCohortVersion } from '@verantos-dev/data-access-cohorts';

import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseConfirmCohortPickArgs, UseConfirmCohortPickResult } from '../types';

// Commits the highlighted pick: warms the new cohort's version query, stages
// it on the definition store, and closes the modal. Isolated from list/search
// and selection state so the submit side effect can be tested independently.
export const useConfirmCohortPick = ({
  projectId,
  cohortList,
  selectedId,
  currentCohortId,
  isLoading,
  onClose,
}: UseConfirmCohortPickArgs): UseConfirmCohortPickResult => {
  const queryClient = useQueryClient();
  const setCohort = useAnalysisDefinitionStore((s) => s.setCohort);

  const onConfirm = useCallback(() => {
    if (!selectedId) return;
    if (selectedId === currentCohortId) {
      onClose();
      return;
    }

    // Warm the cohort version the Study period section reads for the new pick.
    const cohortVersionId = cohortList.find(
      (c) => c.cohortProfileId === selectedId
    )?.latestCohortVersionId;
    if (cohortVersionId) {
      queryClient.prefetchQuery({
        queryKey: cohortQueryKeys.version(cohortVersionId),
        queryFn: () => fetchCohortVersion(projectId, selectedId, cohortVersionId),
      });
    }

    setCohort(selectedId);
    onClose();
  }, [selectedId, currentCohortId, setCohort, onClose, cohortList, queryClient, projectId]);

  const canConfirm = selectedId !== null && selectedId !== currentCohortId && !isLoading;

  return { onConfirm, canConfirm };
};
